import {
	afterNextRender,
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	forwardRef,
	inject,
	Injector,
	input,
	signal,
	viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormField } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslocoPipe } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { CategoryTask } from '../../../api/app-types/category-tast.app';
import { CATEGORIES_API } from '../../../api/injection-tokens/services.token';
import { provideCategoryApi } from '../../../api/services/categories.service';
import { LvrInputDirective } from '../../directives/lvr-input.directive';
import { LvrField } from '../field/field';
import { InfoTooltip } from '../info-tooltip/info-tooltip';

@Component({
	selector: 'lvr-searchable-task-picker',
	templateUrl: './searchable-task-picker.html',
	styleUrl: './searchable-task-picker.scss',
	imports: [
		TranslocoPipe,
		MatFormField,
		MatInputModule,
		MatAutocompleteModule,
		MatCheckboxModule,
		MatButtonModule,
		MatIconModule,
		LvrInputDirective,
		LvrField,
		InfoTooltip,
	],
	providers: [
		provideCategoryApi,
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SearchableTaskPicker),
			multi: true,
		},
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchableTaskPicker implements ControlValueAccessor {
	public readonly phaseId = input<number>();
	public readonly typeOfCareId = input<number | null>(null);
	public readonly mode = input<'create' | 'edit'>('create');
	public readonly placeholder = input<string>('tasks_picker.checklist_title');
	public readonly searchPlaceholder = input<string>('tasks_picker.search_placeholder');
	public readonly noResultsText = input<string>('tasks_picker.no_results');
	public readonly selectedText = input<string>('tasks_picker.selected');

	private readonly env = inject(ENVIRONMENT);
	private readonly api = inject(CATEGORIES_API);
	private readonly selectedIds = signal<number[]>([]);
	public readonly filterText = signal('');
	public readonly focused = signal(false);
	public readonly allTasks = signal<CategoryTask[]>([]);
	public readonly isLoading = signal(false);
	public readonly errorMessage = signal<string | null>(null);
	private readonly disabled = signal(false);
	private readonly autocompleteTrigger = viewChild(MatAutocompleteTrigger);
	private readonly injector = inject(Injector);

	public readonly filteredTasks = computed(() => {
		const term = this.filterText().trim().toLocaleLowerCase();
		if (!term) return this.allTasks();
		return this.allTasks().filter((task) => task.description.toLocaleLowerCase().includes(term));
	});
	public readonly selectedTasks = computed(() => {
		const selected = new Set(this.selectedIds());
		return this.allTasks().filter((task) => selected.has(task.id));
	});
	public readonly displayText = computed(() => {
		if (this.focused()) return this.filterText();
		return this.selectedTasks()
			.map((task) => task.description)
			.join(', ');
	});
	public readonly selectedTaskIds = () => this.selectedIds();

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private onChange: (value: number[]) => void = () => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private onTouched: () => void = () => {};

	public constructor() {
		effect(() => {
			void this.loadTasks(this.phaseId(), this.typeOfCareId());
		});
	}

	public onFocus(): void {
		this.focused.set(true);
	}

	public onBlur(): void {
		this.focused.set(false);
		this.filterText.set('');
		this.onTouched();
	}

	public onSearch(event: Event): void {
		this.filterText.set((event.target as HTMLInputElement).value);
	}

	public clearSearch(): void {
		this.filterText.set('');
	}

	// keeps Material's own trigger-value write in sync so selection never overwrites the search text
	public readonly displayWithTask = (): string => this.filterText();

	public onOptionSelected(task: CategoryTask, input: HTMLInputElement): void {
		const searchText = this.filterText();
		this.toggleTask(task);
		this.filterText.set(searchText);
		input.value = searchText;
		afterNextRender(() => this.autocompleteTrigger()?.openPanel(), { injector: this.injector });
	}

	public isSelected(taskId: number): boolean {
		return this.selectedIds().includes(taskId);
	}

	public toggleTask(task: CategoryTask): void {
		const current = new Set(this.selectedIds());
		if (current.has(task.id)) current.delete(task.id);
		else current.add(task.id);
		this.selectedIds.set([...current]);
		this.onChange(this.selectedIds());
		this.onTouched();
	}

	public writeValue(value: number[] | null): void {
		this.selectedIds.set(value ?? []);
	}

	public registerOnChange(fn: (value: number[]) => void): void {
		this.onChange = fn;
	}

	public registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	public setDisabledState(isDisabled: boolean): void {
		this.disabled.set(isDisabled);
	}

	public isDisabled(): boolean {
		return this.disabled();
	}

	private async loadTasks(phaseId?: number, typeOfCareId?: number | null): Promise<void> {
		this.isLoading.set(true);
		this.errorMessage.set(null);
		try {
			const pagination = this.env.pagination ?? { offset: 0, range: 50 };
			const range = Math.max(1, pagination.range);
			let offset = pagination.offset;
			const tasks: CategoryTask[] = [];
			let total = Number.POSITIVE_INFINITY;

			while (tasks.length < total) {
				const response = await this.api.getAllCategoryTasks(offset, range, phaseId, typeOfCareId ?? undefined);
				const page = response.entities ?? [];
				tasks.push(...page);
				total = response.totalEntities ?? tasks.length;
				if (page.length === 0 || page.length < range) break;
				offset += range;
			}

			const uniqueTasks = [...new Map(tasks.map((task) => [task.id, task])).values()];
			this.allTasks.set(uniqueTasks);
			const validIds = new Set(uniqueTasks.map((task) => task.id));
			const nextIds = this.selectedIds().filter((id) => validIds.has(id));
			if (nextIds.length !== this.selectedIds().length) {
				this.selectedIds.set(nextIds);
				this.onChange(nextIds);
			}
		} catch (error) {
			console.error(error);
			this.errorMessage.set('tasks_picker.load_error');
		} finally {
			this.isLoading.set(false);
		}
	}
}
